import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { fetchStockList } from '../api';
import { useLatestFinancialRatios } from '../hooks/useLatestFinancialRatios';
import FinancialRatioStatus, { FinancialRatiosExplanation, FinancialRatioValue } from '../components/FinancialRatioStatus';
import ScreenerFilter from '../components/ScreenerFilter';
import InfoTooltip from '../components/InfoTooltip';
import MetricSelector from '../components/MetricSelector';
import { StockComparisonContext } from '../contexts/StockComparisonContext';
import { StockScreenerContext } from '../contexts/StockScreenerContext';
import definitions from '../constants/metricsDefinitions';
import { METRIC_FIELDS } from '../constants/metricFields';
import './StockScreener.css';

const ALL_METRICS = METRIC_FIELDS
  .map(metric => ({ label: metric.label, key: metric.value }))
  .sort((a, b) => a.label.localeCompare(b.label));

export default function StockScreener() {
  const navigate = useNavigate();
  const auth = useAuth();
  const {
    screenedStocks, selectedForComparison, selectedMetrics, activeFilters,
    removeStock, toggleSelection, clearSelection, setSelectedMetrics,
    setActiveFilters, setScreenedStocks
  } = useContext(StockScreenerContext);
  const { setSelectedSymbols } = useContext(StockComparisonContext);
  const [universe, setUniverse] = useState(null);
  const [universeError, setUniverseError] = useState(false);
  const token = auth?.user?.id_token;
  const allowedStocks = universe && universe.token === token ? universe.symbols : [];
  const visibleStocks = screenedStocks.filter(symbol => allowedStocks.includes(symbol));
  const comparisonStocks = selectedForComparison.filter(symbol => visibleStocks.includes(symbol));
  const { results, metricsData } = useLatestFinancialRatios(visibleStocks);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isFilterSidebarCollapsed, setIsFilterSidebarCollapsed] = useState(false);

  // Re-evaluate entitlements when login changes; ignore stale responses.
  useEffect(() => {
    let cancelled = false;
    setUniverseError(false);
    fetchStockList(token).then(json => {
      if (!Array.isArray(json)) throw new Error('Invalid stock universe');
      const symbols = [...new Set(json.map(item =>
        (item.symbol || item.ticker || '').toUpperCase()).filter(Boolean))];
      if (cancelled) return;
      setUniverse({ token, symbols });
      setScreenedStocks(symbols);
    }).catch(() => {
      if (!cancelled) setUniverseError(true);
    });
    return () => { cancelled = true; };
  }, [token, setScreenedStocks]);

  const removeFromScreener = (symbol) => removeStock(symbol);
  const handleFilterChange = useCallback(filters => setActiveFilters(filters), [setActiveFilters]);
  const getFilteredStocks = () => visibleStocks.filter(symbol =>
    (activeFilters || []).every(filter => {
      const value = metricsData[symbol]?.[filter.metric];
      return Number.isFinite(value) && value >= filter.min && value <= filter.max;
    })
  );

  // 5️⃣ Send selected screener stocks to comparison page
  const handleCompare = () => {
    if (comparisonStocks.length === 0) return;

    setSelectedSymbols(comparisonStocks);
    clearSelection();
    navigate('/comparison');
  };

  useEffect(() => {
    if (!isFilterDrawerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsFilterDrawerOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFilterDrawerOpen]);

  const filteredStocks = getFilteredStocks();
  const activeFilterCount = activeFilters?.length || 0;
  const pageClassName = [
    'stock-screener-page',
    isFilterDrawerOpen ? 'filter-drawer-open' : '',
    isFilterSidebarCollapsed ? 'filter-sidebar-collapsed' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={pageClassName}>
      <div className="main-container">
        <div className="content-area">
          <h2>
            Stock Screener - More stocks coming very soon! 
          </h2>

          <FinancialRatiosExplanation />
          {universeError && <p role="alert">Could not load available stocks. Reload to retry.</p>}
          {!universeError && (!universe || universe.token !== token) && <p role="status">Loading available stocks…</p>}

          {/* Results Summary */}
          {visibleStocks.length > 0 && (
            <div className="results-summary">
              <p>
                Screening {visibleStocks.length} stocks
                {activeFilters && activeFilters.length > 0 &&
                  `, ${filteredStocks.length} match your criteria`}
              </p>
            </div>
          )}

          {/* Screener Table */}
          {visibleStocks.length > 0 ? (
            <div className="table-container">
              <div className="screener-actions">
                <button
                  className="compare-btn"
                  disabled={comparisonStocks.length === 0}
                  onClick={handleCompare}
                >
                  Compare Selected ({comparisonStocks.length})
                </button>
                <button
                  type="button"
                  className="filter-drawer-toggle"
                  onClick={() => {
                    setIsFilterSidebarCollapsed(false);
                    setIsFilterDrawerOpen(true);
                  }}
                  aria-controls="screener-filter-panel"
                  aria-expanded={isFilterDrawerOpen}
                >
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </button>
              </div>

              <MetricSelector
                id="screener-metric-selector"
                metrics={ALL_METRICS}
                selectedMetrics={selectedMetrics}
                setSelectedMetrics={setSelectedMetrics}
              />

              <div className="table-scroll">
                <table className="screener-table">
                  <thead>
                    <tr>
                      <th className="select-column">Select</th>
                      <th className="stock-column">Stock</th>
                      {selectedMetrics.map((metric) => (
                        <th key={metric.key} className="metric-column">
                          <div className="metric-header">
                            {metric.label}
                            <InfoTooltip
                              text={
                                definitions[metric.key] || 'No description'
                              }
                            />
                          </div>
                        </th>
                      ))}
                      <th className="action-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStocks.map((stock) => (
                      <tr key={stock}>
                        <td className="select-cell">
                          <input
                            type="checkbox"
                            checked={selectedForComparison.includes(stock)}
                            onChange={() => toggleSelection(stock)}
                          />
                        </td>
                        <td className="stock-cell">{stock}<FinancialRatioStatus result={results[stock]} /></td>
                        {selectedMetrics.map((metric) => (
                          <td
                            key={`${stock}-${metric.key}`}
                            className="metric-cell"
                          >
                            <FinancialRatioValue result={results[stock]} metric={metric.key} />
                          </td>
                        ))}
                        <td className="action-cell">
                          <button
                            className="remove-button"
                            onClick={() => removeFromScreener(stock)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredStocks.length === 0 &&
                activeFilters &&
                activeFilters.length > 0 && (
                  <div className="no-results">
                    <p>
                      No loaded stocks match your filter criteria. Missing values are excluded;
                      try adjusting your filters or wait for loading to finish.
                    </p>
                  </div>
                )}
            </div>
          ) : universe && universe.token === token && !universeError ? (
            <div className="empty-screener">
              <p>No stocks available to screen.</p>
            </div>
          ) : null}
        </div>

        <aside
          className="filter-sidebar"
          id="screener-filter-panel"
          aria-label="Stock screener filters"
        >
          <div className="filter-drawer-actions">
            <button
              type="button"
              className="filter-drawer-close"
              onClick={() => setIsFilterDrawerOpen(false)}
            >
              Close
            </button>
          </div>
          <ScreenerFilter
            onFilterChange={handleFilterChange}
            activeFilters={activeFilters}
            metricsData={metricsData}
            isCollapsed={isFilterSidebarCollapsed}
            onCollapsedChange={setIsFilterSidebarCollapsed}
            collapseDirection="horizontal"
          />
        </aside>
      </div>

      <button
        type="button"
        className="filter-drawer-overlay"
        onClick={() => setIsFilterDrawerOpen(false)}
        aria-label="Close filters"
        hidden={!isFilterDrawerOpen}
      />
    </div>
  );
}
